import { Kegiatan, KegiatanAbsen } from '../types';

/**
 * Helper mapping snake_case (Supabase/Postgres) ↔ camelCase (tipe TS `Kegiatan`/`KegiatanAbsen`).
 * Dipakai di KegiatanSantriContext (Fase 2) dan 3 file pembaca langsung (Fase 3)
 * supaya bentuk data yang dikembalikan ke consumer TETAP camelCase (28+ file tidak berubah).
 */

const toDate = (v: unknown): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return v;
    const d = new Date(v as string);
    return isNaN(d.getTime()) ? undefined : d;
};

const toISO = (v: unknown): string | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return v.toISOString();
    const d = new Date(v as string);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
};

const normalizeArray = <T>(v: unknown): T[] => {
    if (Array.isArray(v)) return v as T[];
    if (typeof v === 'string' && v.length > 0) return [v] as unknown as T[];
    return [];
};

// ---------------------------------------------------------------------------
// kegiatan  (tabel `kegiatan`)
// ---------------------------------------------------------------------------
export const mapKegiatanFromDb = (row: any): Kegiatan => ({
    id: row.id,
    namaKegiatan: row.nama_kegiatan ?? row.namaKegiatan,
    lokasi: normalizeArray<'Putra' | 'Putri'>(row.lokasi),
    jenjang: normalizeArray<'SMP' | 'SMA'>(row.jenjang),
    jenis: row.jenis,
    isActive: row.is_active ?? row.isActive ?? true,
    isParenting: row.is_parenting ?? row.isParenting,
    metodeAbsensi: row.metode_absensi ?? row.metodeAbsensi,
    tempat: normalizeArray<string>(row.tempat),
    hari: normalizeArray<string>(row.hari),
    tanggalMulai: toDate(row.tanggal_mulai ?? row.tanggalMulai),
    tanggalAkhir: toDate(row.tanggal_akhir ?? row.tanggalAkhir),
    jamMulai: row.jam_mulai ?? row.jamMulai ?? '00:00',
    jamAkhir: row.jam_akhir ?? row.jamAkhir ?? '00:00',
    toleransiTerlambat: row.toleransi_terlambat ?? row.toleransiTerlambat,
    absenBisaDimulaiSebelum: row.absen_bisa_dimulai_sebelum ?? row.absenBisaDimulaiSebelum,
    absenDitutupSetelah: row.absen_ditutup_setelah ?? row.absenDitutupSetelah,
    targetPeserta: row.target_peserta ?? row.targetPeserta,
    kelasPeserta: normalizeArray<string>(row.kelas_peserta ?? row.kelasPeserta),
    exceptions: row.exceptions ?? undefined,
    isApresiasiIzin: row.is_apresiasi_izin ?? row.isApresiasiIzin ?? false,
    apresiasiJenisIzin: row.apresiasi_jenis_izin ?? row.apresiasiJenisIzin,
    apresiasiTanggalMulai: toDate(row.apresiasi_tanggal_mulai ?? row.apresiasiTanggalMulai),
    apresiasiJamMulai: row.apresiasi_jam_mulai ?? row.apresiasiJamMulai,
    apresiasiTanggalSelesai: toDate(row.apresiasi_tanggal_selesai ?? row.apresiasiTanggalSelesai),
    apresiasiJamSelesai: row.apresiasi_jam_selesai ?? row.apresiasiJamSelesai,
    apresiasiAlasan: row.apresiasi_alasan ?? row.apresiasiAlasan,
    apresiasiTujuan: row.apresiasi_tujuan ?? row.apresiasiTujuan,
    apresiasiSkipReview: row.apresiasi_skip_review ?? row.apresiasiSkipReview ?? false,
});

export const mapKegiatanToDb = (data: Partial<Kegiatan>): Record<string, any> => {
    const out: Record<string, any> = {};
    if (data.namaKegiatan !== undefined) out.nama_kegiatan = data.namaKegiatan;
    if (data.lokasi !== undefined) out.lokasi = data.lokasi;
    if (data.jenjang !== undefined) out.jenjang = data.jenjang;
    if (data.jenis !== undefined) out.jenis = data.jenis;
    if (data.isActive !== undefined) out.is_active = data.isActive;
    if (data.isParenting !== undefined) out.is_parenting = data.isParenting;
    if (data.metodeAbsensi !== undefined) out.metode_absensi = data.metodeAbsensi;
    if (data.tempat !== undefined) out.tempat = data.tempat;
    if (data.hari !== undefined) out.hari = data.hari;
    if (data.tanggalMulai !== undefined) out.tanggal_mulai = toISO(data.tanggalMulai);
    if (data.tanggalAkhir !== undefined) out.tanggal_akhir = toISO(data.tanggalAkhir);
    if (data.jamMulai !== undefined) out.jam_mulai = data.jamMulai;
    if (data.jamAkhir !== undefined) out.jam_akhir = data.jamAkhir;
    if (data.toleransiTerlambat !== undefined) out.toleransi_terlambat = data.toleransiTerlambat;
    if (data.absenBisaDimulaiSebelum !== undefined) out.absen_bisa_dimulai_sebelum = data.absenBisaDimulaiSebelum;
    if (data.absenDitutupSetelah !== undefined) out.absen_ditutup_setelah = data.absenDitutupSetelah;
    if (data.targetPeserta !== undefined) out.target_peserta = data.targetPeserta;
    if (data.kelasPeserta !== undefined) out.kelas_peserta = data.kelasPeserta;
    if (data.exceptions !== undefined) out.exceptions = data.exceptions;
    if (data.isApresiasiIzin !== undefined) out.is_apresiasi_izin = data.isApresiasiIzin;
    if (data.apresiasiJenisIzin !== undefined) out.apresiasi_jenis_izin = data.apresiasiJenisIzin;
    if (data.apresiasiTanggalMulai !== undefined) out.apresiasi_tanggal_mulai = toISO(data.apresiasiTanggalMulai);
    if (data.apresiasiJamMulai !== undefined) out.apresiasi_jam_mulai = data.apresiasiJamMulai;
    if (data.apresiasiTanggalSelesai !== undefined) out.apresiasi_tanggal_selesai = toISO(data.apresiasiTanggalSelesai);
    if (data.apresiasiJamSelesai !== undefined) out.apresiasi_jam_selesai = data.apresiasiJamSelesai;
    if (data.apresiasiAlasan !== undefined) out.apresiasi_alasan = data.apresiasiAlasan;
    if (data.apresiasiTujuan !== undefined) out.apresiasi_tujuan = data.apresiasiTujuan;
    if (data.apresiasiSkipReview !== undefined) out.apresiasi_skip_review = data.apresiasiSkipReview;
    return out;
};

// ---------------------------------------------------------------------------
// kegiatan_absen  (tabel `kegiatan_absen`)
// ---------------------------------------------------------------------------
export const mapKegiatanAbsenFromDb = (row: any): KegiatanAbsen => ({
    id: row.id,
    kegiatanId: row.kegiatan_id ?? row.kegiatanId,
    namaKegiatan: row.nama_kegiatan ?? row.namaKegiatan,
    studentId: row.student_id ?? row.studentId,
    studentName: row.student_name ?? row.studentName,
    kelas: row.kelas ?? '',
    status: row.status ?? 'Hadir',
    tanggal: toDate(row.tanggal) ?? new Date(),
    petugas: row.petugas ?? '',
    tahunAjaran: row.tahun_ajaran ?? row.tahunAjaran,
    semester: row.semester,
    isParenting: row.is_parenting ?? row.isParenting,
    tamu: row.tamu,
    label: row.label,
});

export const mapKegiatanAbsenToDb = (data: Partial<KegiatanAbsen>): Record<string, any> => {
    const out: Record<string, any> = {};
    if (data.kegiatanId !== undefined) out.kegiatan_id = data.kegiatanId;
    if (data.namaKegiatan !== undefined) out.nama_kegiatan = data.namaKegiatan;
    if (data.studentId !== undefined) out.student_id = data.studentId;
    if (data.studentName !== undefined) out.student_name = data.studentName;
    if (data.kelas !== undefined) out.kelas = data.kelas;
    if (data.status !== undefined) out.status = data.status;
    if (data.tanggal !== undefined) out.tanggal = toISO(data.tanggal);
    if (data.petugas !== undefined) out.petugas = data.petugas;
    if (data.tahunAjaran !== undefined) out.tahun_ajaran = data.tahunAjaran;
    if (data.semester !== undefined) out.semester = data.semester;
    if (data.isParenting !== undefined) out.is_parenting = data.isParenting;
    if (data.tamu !== undefined) out.tamu = data.tamu;
    if (data.label !== undefined) out.label = data.label;
    return out;
};
